import { useLinkDossierToSpoorwebOg } from "@/entities/dossier";
import { useGetAvailableOngewensteGebeurtenissen } from "@/features/dossier-management/hooks";
import {
    Button,
    Dialog,
    DialogBody,
    DialogContainer,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    ErrorDisplay,
    Form,
    FormFieldAutocomplete,
    Helptext,
} from "@/shared/components";
import { zodResolver } from "@hookform/resolvers/zod";
import { Dispatch, SetStateAction } from "react";
import { SubmitHandler, useForm, useWatch } from "react-hook-form";
import { z } from "zod";
import { OngewensteGebeurtenisOptionLabel, OngewensteGebeurtenisSummaryCard } from "../shared";

const linkToOgSchema = z.object({
    spoorwebId: z.string(),
});

type LinkToOgData = z.infer<typeof linkToOgSchema>;

type LinkToOGDialogProps = {
    dossierId: string;
    open: boolean;
    onOpenChange: Dispatch<SetStateAction<boolean>>;
};

export function LinkToOGDialog({ dossierId, open, onOpenChange }: LinkToOGDialogProps) {
    const patchDetails = useLinkDossierToSpoorwebOg();
    const { options: ogOptions } = useGetAvailableOngewensteGebeurtenissen();

    const form = useForm<LinkToOgData>({
        resolver: zodResolver(linkToOgSchema),
        defaultValues: {
            spoorwebId: "",
        },
    });

    const spoorwebId = useWatch({ control: form.control, name: "spoorwebId" });
    const selectedOgOption = ogOptions.find((og) => og.spoorwebNr === spoorwebId)?.og;

    const handleSubmit: SubmitHandler<LinkToOgData> = (data) => {
        patchDetails.mutate({ dossierId, spoorwebId: data.spoorwebId }, { onSuccess: () => onOpenChange(false) });
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContainer>
                <DialogHeader>
                    <DialogTitle>Spoorweb koppelen aan dossier</DialogTitle>
                    <DialogDescription className="sr-only">Koppel dit dossier aan een ongewenste gebeurtenis in spoorweb</DialogDescription>
                </DialogHeader>
                <Form {...form}>
                    <form onSubmit={(event) => void form.handleSubmit(handleSubmit)(event)}>
                        <DialogBody className="grid gap-4">
                            <div className="space-y-2">
                                {!spoorwebId && (
                                    <FormFieldAutocomplete
                                        form={form}
                                        name="spoorwebId"
                                        label="Spoorwebnummer"
                                        options={ogOptions}
                                        placeholder="Zoek spoorwebnummer"
                                        renderOption={(option) => (
                                            <OngewensteGebeurtenisOptionLabel
                                                meldingtekst={option.meldingtekst}
                                                spoorwebNr={option.spoorwebNr}
                                                TIS={option.TIS}
                                                timeSinceEvent={option.timeSinceEvent}
                                            />
                                        )}
                                        className="max-w-sm"
                                        data-testid="spoorwebitems"
                                    />
                                )}
                                {spoorwebId && (
                                    <>
                                        <OngewensteGebeurtenisSummaryCard
                                            ongewensteGebeurtenis={selectedOgOption}
                                            betrokkenTreinen={selectedOgOption?.betrokkenTreinen}
                                            spoorwebStatus={selectedOgOption?.status}
                                        />
                                        <Helptext>
                                            Bij het koppelen wordt dit dossier aangevuld met informatie uit Spoorweb. Voor Prognoses, VSM's
                                            en Opstartplannen die al in Spoorweb staan, krijg je acties om te verwerken.
                                        </Helptext>
                                    </>
                                )}
                            </div>
                            {patchDetails.error && (
                                <ErrorDisplay error={patchDetails.error} title="Fout bij het koppelen van ongewenste gebeurtenis" />
                            )}
                        </DialogBody>

                        {spoorwebId && (
                            <DialogFooter>
                                <div className="flex w-full items-center justify-between gap-2">
                                    <Button type="button" variant="tertiary" onClick={() => form.setValue("spoorwebId", "")}>
                                        Vorige
                                    </Button>
                                    <Button type="submit" variant="primary" disabled={patchDetails.isPending}>
                                        Spoorweb koppelen
                                    </Button>
                                </div>
                            </DialogFooter>
                        )}
                    </form>
                </Form>
            </DialogContainer>
        </Dialog>
    );
}
